import { PrimaryButton, SecondaryButton } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { PortableTextBlock } from '@portabletext/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import useIsSsr from '../../../../../../hooks/useIsSsr'
import Spinner from '../../../../../Spinner'
import { blocksToHtml, htmlToBlocks } from './util'

import 'react-quill/dist/quill.snow.css'

const ReactQuillLazy = React.lazy(() => import('react-quill'))

const modules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    // [{ script: 'sub' }, { script: 'super' }], // note: not supported by portable text
    ['clean'],
  ],
}

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'script',
]

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.grayscale.white};
  margin-bottom: 10px;
`

const StyledSecondaryButton = styled(SecondaryButton)`
  margin: 10px 0 0 0;
  @media (min-width: 416px) {
    margin: 0 0 0 10px;
  }
`

export interface TextEditorProps {
  sanityValue: PortableTextBlock[] | null | undefined
  defaultValue: PortableTextBlock[] | null | undefined
  handleSubmit:
    | ((blocks: PortableTextBlock[]) => Promise<void>)
    | ((blocks: PortableTextBlock[]) => void)
  loading: boolean
  submitDisabled: boolean
}

const TextEditor: React.FC<TextEditorProps> = ({
  sanityValue,
  defaultValue,
  handleSubmit,
  loading,
  submitDisabled,
}) => {
  const isSsr = useIsSsr()
  const defaultValueHtml = useMemo(
    () => (defaultValue ? blocksToHtml(defaultValue) : ''),
    [defaultValue],
  )
  const [value, setValue] = useState<string>(() =>
    sanityValue ? blocksToHtml(sanityValue) : defaultValueHtml,
  )
  const [enableSubmit, setEnableSubmit] = useState(false)

  useEffect(() => {
    setEnableSubmit(false)
  }, [sanityValue])

  const onChange = useCallback((html: string) => {
    setValue(html)
    setEnableSubmit(true)
  }, [])

  const onSubmit = useCallback(() => {
    const blocks = htmlToBlocks(value)
    handleSubmit(blocks)
  }, [handleSubmit, value])

  const onClickSetDefault = useCallback(() => {
    setValue(defaultValueHtml)
    setEnableSubmit(true)
  }, [defaultValueHtml])

  if (isSsr) return <React.Fragment />

  return (
    <React.Suspense fallback={<Spinner loading />}>
      <Container>
        <ReactQuillLazy
          theme="snow"
          value={value}
          modules={modules}
          formats={formats}
          onChange={onChange}
          placeholder="Click to add text"
        />
      </Container>
      <PrimaryButton disabled={!enableSubmit || loading || submitDisabled} onClick={onSubmit}>
        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : enableSubmit ? (
          'Publish Changes'
        ) : (
          'No Changes to Publish'
        )}
      </PrimaryButton>
      <StyledSecondaryButton
        disabled={loading || value === defaultValueHtml}
        onClick={onClickSetDefault}
      >
        Reset to default
      </StyledSecondaryButton>
    </React.Suspense>
  )
}

export default TextEditor
